import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

interface UserSubscriptionData {
  remaining_iban_validations: number;
  remaining_bank_info_requests: number;
  plans?: { name: string } | null;
  api_keys?: { api_key: string }[];
  start_date?: string | null;
  end_date?: string | null;
}

const Dashboard = () => {
  const [userData, setUserData] = useState<{
    firstName: string;
    planName: string;
    ibanValidations: number;
    bankInfoRequests: number;
    apiKey: string;
    startDate: string | null;
    endDate: string | null;
  } | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: user, error: userError } = await supabase.auth.getUser();

        if (userError || !user?.user) {
          setError("User not logged in.");
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from("user_subscriptions")
          .select(`
            remaining_iban_validations,
            remaining_bank_info_requests,
            plans(name),
            api_keys(api_key),
            start_date,
            end_date
          `)
          .eq("user_id", user.user.id)
          .single();

        if (error) {
          setError("Error fetching subscription data.");
          setLoading(false);
          return;
        }

        const typedData = data as unknown as UserSubscriptionData;

        const planName = typedData?.plans?.name ?? "Unknown Plan";
        const apiKey = Array.isArray(typedData?.api_keys) && typedData.api_keys.length
          ? typedData.api_keys[0].api_key
          : "No API Key";

        setUserData({
          firstName: user.user.user_metadata.first_name || "User",
          planName,
          ibanValidations: typedData?.remaining_iban_validations ?? 0,
          bankInfoRequests: typedData?.remaining_bank_info_requests ?? 0,
          apiKey,
          startDate: typedData?.start_date ?? null,
          endDate: typedData?.end_date ?? null,
        });

        setLoading(false);
      } catch (err) {
        setError("Unexpected error occurred.");
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleCopy = () => {
    if (userData?.apiKey) {
      navigator.clipboard.writeText(userData.apiKey);
      setCopied(true);
  
      setTimeout(() => {
        setCopied(false);
      }, 2000); // Show "Copied!" for 2 seconds
    }
  }; 

  const handleRegenerate = async () => {
    const { data: user } = await supabase.auth.getUser();
    const userId = user?.user?.id;

    if (!userId) {
      alert("You must be logged in.");
      return;
    }

    const { data, error } = await supabase.rpc("regenerate_api_key", {
      p_user_id: userId,
    });

    if (error) {
      console.error("Failed to regenerate API key:", error);
      alert("Error regenerating API key.");
      return;
    }

    setUserData((prev) =>
      prev ? { ...prev, apiKey: data } : prev
    );
  };

  return (
    <div className="max-w-3xl mx-auto py-12 px-6">
      <h1 className="text-3xl font-bold text-center text-teal-600 mb-6">
        API Dashboard
      </h1>

      {loading ? (
        <p className="text-center text-gray-500">Loading...</p>
      ) : error ? (
        <p className="text-red-500 text-center">{error}</p>
      ) : (
        <div className="grid gap-6">
          {/* User Info */}
          <div className="bg-white shadow-lg rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800">
              Welcome, {userData?.firstName}!
            </h2>
            <p className="text-gray-500">
              You're on the{" "}
              <strong className="text-teal-600">{userData?.planName}</strong> plan.
            </p>

            {/* Plan-specific info */}
            {userData?.planName === "Free Tier" && userData.startDate ? (
              <p className="text-gray-500">
                Credits will renew on{" "}
                <strong className="text-teal-600">
                  {new Date(new Date(userData.startDate).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                </strong>
              </p>
            ) : userData?.endDate ? (
              <p className="text-gray-500">
                Subscription valid until{" "}
                <strong className="text-teal-600">
                  {new Date(userData.endDate).toLocaleDateString()}
                </strong>
              </p>
            ) : userData?.planName === "Pay As You Go" ? (
              <p className="text-gray-500">
                Pay-as-you-go: credits are available until used.
              </p>
            ) : null}
          </div>

          {/* API Key with Actions */}
          <div className="bg-white shadow-lg rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Your API Key</h3>

            <p className="text-sm bg-gray-100 p-3 rounded-lg text-gray-700 break-all mb-3">
              {userData?.apiKey}
            </p>

            <div className="flex gap-3">
            <button
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded"
              onClick={handleCopy}
            >
              {copied ? "Copied!" : "Copy"}
            </button>

              <button
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded"
                onClick={handleRegenerate}
              >
                Regenerate
              </button>
            </div>
          </div>

          {/* Usage Stats */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white shadow-lg rounded-lg p-6 text-center">
              <h4 className="text-lg font-semibold text-gray-800">
                IBAN Validations
              </h4>
              <p className="text-3xl font-bold text-teal-600">
                {userData?.ibanValidations}
              </p>
            </div>
            <div className="bg-white shadow-lg rounded-lg p-6 text-center">
              <h4 className="text-lg font-semibold text-gray-800">
                Bank Info Requests
              </h4>
              <p className="text-3xl font-bold text-teal-600">
                {userData?.bankInfoRequests}
              </p>
            </div>
          </div>

          {/* Support */}
          <div className="bg-gray-100 text-center py-6 rounded-lg">
            <p className="text-gray-600">
              Need help?{" "}
              <a
                href="mailto:support@iban-analyzer.com"
                className="text-teal-600 font-semibold"
              >
                Contact Support
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
